import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import type React from 'react';
import { PERMISSIONS } from '../../shared/constants/permissions';

interface TenderPermissionsFormProps {
  permissions: string[];
  onChange: (newPermissions: string[]) => void;
  hideNoneOption?: boolean;
}

export const TenderPermissionsForm: React.FC<TenderPermissionsFormProps> = ({
  permissions,
  onChange,
  hideNoneOption = false,
}) => {
  const hasFullAccess = permissions.includes(PERMISSIONS.TENDER_FULL_ACCESS);

  const handleToggle = (permission: string) => {
    onChange(
      permissions.includes(permission)
        ? permissions.filter((p) => p !== permission)
        : [...permissions, permission]
    );
  };

  const handleEditAccessChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const newPerms = permissions.filter(
      (p) => p !== PERMISSIONS.TENDER_EDIT && p !== PERMISSIONS.DRAFT_EDIT
    );
    if (value) {
      newPerms.push(value);
    }
    onChange(newPerms);
  };

  const editAccessValue = hasFullAccess
    ? PERMISSIONS.DRAFT_EDIT
    : permissions.includes(PERMISSIONS.DRAFT_EDIT)
      ? PERMISSIONS.DRAFT_EDIT
      : permissions.includes(PERMISSIONS.TENDER_EDIT)
        ? PERMISSIONS.TENDER_EDIT
        : '';

  return (
    <FormGroup>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 1, mb: 0.5, fontWeight: 'bold' }}
      >
        Access Level
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={hasFullAccess}
            onChange={() => handleToggle(PERMISSIONS.TENDER_FULL_ACCESS)}
            size="small"
            color="primary"
          />
        }
        label={
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            Full Access
          </Typography>
        }
      />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 1, mb: 0.5, fontWeight: 'bold' }}
      >
        View Access
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={hasFullAccess || permissions.includes(PERMISSIONS.TENDER_SECTIONS_VIEW_CLIENT)}
            onChange={() => handleToggle(PERMISSIONS.TENDER_SECTIONS_VIEW_CLIENT)}
            disabled={hasFullAccess}
            size="small"
          />
        }
        label={<Typography variant="body2">View All Sections</Typography>}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={hasFullAccess || permissions.includes(PERMISSIONS.TENDER_FILES_VIEW_CLIENT)}
            onChange={() => handleToggle(PERMISSIONS.TENDER_FILES_VIEW_CLIENT)}
            disabled={hasFullAccess}
            size="small"
          />
        }
        label={<Typography variant="body2">View All Files</Typography>}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={hasFullAccess || permissions.includes(PERMISSIONS.LIBRARY_VIEW_CLIENT)}
            onChange={() => handleToggle(PERMISSIONS.LIBRARY_VIEW_CLIENT)}
            disabled={hasFullAccess}
            size="small"
          />
        }
        label={<Typography variant="body2">View Library</Typography>}
      />

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 1.5, mb: 0.5, fontWeight: 'bold' }}
      >
        Edit Access
      </Typography>
      <RadioGroup value={editAccessValue} onChange={handleEditAccessChange}>
        {!hideNoneOption && (
          <FormControlLabel
            value=""
            control={<Radio size="small" />}
            label={<Typography variant="body2">None</Typography>}
            disabled={hasFullAccess}
          />
        )}
        <FormControlLabel
          value={PERMISSIONS.TENDER_EDIT}
          control={<Radio size="small" />}
          label={<Typography variant="body2">Edit Only Assigned Sections</Typography>}
          disabled={hasFullAccess}
        />
        <FormControlLabel
          value={PERMISSIONS.DRAFT_EDIT}
          control={<Radio size="small" />}
          label={<Typography variant="body2">Edit All Sections</Typography>}
          disabled={hasFullAccess}
        />
      </RadioGroup>
    </FormGroup>
  );
};
